import React from 'react';
import PropTypes from 'prop-types';
import {computed, observable} from "mobx";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Skeleton from "@material-ui/lab/Skeleton";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import GetAppIcon from '@material-ui/icons/GetApp';
import EditIcon from '@material-ui/icons/Edit';

import Scenario from "../../api/Scenario";
import DataFrame from "../../components/utils/DataFrame";
import { statGreen, statRed } from "../../globals";
import { getFileName } from "../../utils";
import Version from "../../api/Version";


const styles = theme => ({
  added: {
    backgroundColor: statGreen,
    color: 'white',
  },
  removed: {
    backgroundColor: statRed,
    color: 'white',
  },
});


@withStyles(styles)
@observer
class ScenarioInputsComparison extends React.Component {
  static propTypes = {
    scenario: PropTypes.instanceOf(Scenario).isRequired,
    otherScenario: PropTypes.instanceOf(Scenario)
  };
  static defaultProps = {
    otherScenario: null,
  };

  @observable comparison = null;

  async componentDidMount() {
    const { scenario } = this.props;
    this.comparison = await scenario.ajaxRequestData('get', '/compare/', {});
  }

  @computed get otherScenario() {
    const { otherScenario } = this.props;
    if (otherScenario === null) {
      return Scenario.getForId(this.comparison.other_scenario);
    }
    return otherScenario;
  }

  render() {
    if (this.comparison === null) {
      return <Skeleton variant="rect" height={200} />;
    }
    const { scenario, classes } = this.props;
    const { otherScenario } = this;
    if (otherScenario === null) {
      return <Typography variant="h6">Could not find any successful scenario to compare with.</Typography>
    }
    const dataFrameLimit = 5;
    return (
      <Grid container spacing={2} direction="column" wrap="nowrap">
        <Grid item>
          <Grid container justify="space-between" spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="h5">Comparison with the closest successful scenario</Typography>
              <Typography variant="h6">{otherScenario.toString()}</Typography>
            </Grid>
            <Grid item>
              <Tooltip title={otherScenario.toString()}>
                <Button component={Link} to={otherScenario.editUrl} startIcon={<EditIcon />}>
                  <Typography noWrap>Edit closest scenario</Typography>
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {Object.entries(this.comparison.changed_files).map(([versionName, diffData]) =>
            <Accordion key={versionName}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    “{diffData.name}” changed
                  </Grid>
                  {diffData.added_keys && (
                    <Grid item>
                      <Chip
                        label={`${diffData.added_keys_count} added key·s`}
                        className={classes.added}
                        size="small"
                      />
                    </Grid>
                  )}
                  {diffData.removed_keys && (
                    <Grid item>
                      <Chip
                        label={`${diffData.removed_keys_count} removed key·s`}
                        className={classes.removed}
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container direction="column" spacing={2} wrap="nowrap">
                  <Grid item container spacing={2}>
                    <Grid item xs>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<GetAppIcon />}
                        href={diffData.file}
                      >
                        <Typography noWrap>This scenario: {getFileName(diffData.file)}</Typography>
                      </Button>
                    </Grid>
                    <Grid item xs>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<GetAppIcon />}
                        href={diffData.other_file}
                      >
                        <Typography noWrap>Closest scenario: {getFileName(diffData.other_file)}</Typography>
                      </Button>
                    </Grid>
                  </Grid>
                  {diffData.added_keys && (
                    <>
                      <Grid item>
                        <Typography variant="h6">Added keys</Typography>
                      </Grid>
                      <Grid item>
                        <DataFrame serializedDataFrame={diffData.added_keys} limit={dataFrameLimit} />
                      </Grid>
                    </>
                  )}
                  {diffData.removed_keys && (
                    <>
                      <Grid item>
                        <Typography variant="h6">Removed keys</Typography>
                      </Grid>
                      <Grid item>
                        <DataFrame serializedDataFrame={diffData.removed_keys} limit={dataFrameLimit} />
                      </Grid>
                    </>
                  )}
                  {(diffData.added_keys_count > 5 || diffData.removed_keys_count > 5) && (
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<GetAppIcon />}
                        href={Version.getForId(diffData.pk).getApiUrl(
                          `/compare/?other_version=${diffData.other_pk}&export=xlsx`
                        )}
                      >
                        Download added & removed keys
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {this.comparison.changed_parameters.length} parameter·s changed
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Parameter name</TableCell>
                    <TableCell>This scenario</TableCell>
                    <TableCell>Closest scenario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.comparison.changed_parameters.map(parameter => {
                    let value = scenario[parameter];
                    let otherValue = otherScenario[parameter];
                    return (
                      <TableRow key={parameter}>
                        <TableCell>{Scenario.fieldsData[parameter].label}</TableCell>
                        <TableCell>{value === null || value === '' ? '–' : `${value}`}</TableCell>
                        <TableCell>{otherValue === null || otherValue === '' ? '–' : `${otherValue}`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    );
  }
}


export default ScenarioInputsComparison;
